/**
 * Shortcuts Modal - Phase 11
 * Displays keyboard shortcuts help
 */
import { useState, useEffect } from 'react'
import { Modal } from './Modal'
import './ShortcutsModal.css'

interface ShortcutItem {
  keys: string[]
  description: string
}

const SHORTCUTS: { section: string; items: ShortcutItem[] }[] = [
  {
    section: 'Global',
    items: [
      { keys: ['Ctrl', 'K'], description: 'Open search / command palette' },
      { keys: ['Ctrl', '.'], description: 'Toggle AI panel' },
      { keys: ['Ctrl', ','], description: 'Open settings' },
      { keys: ['['], description: 'Toggle sidebar' },
      { keys: ['/'], description: 'Focus search (alternative)' },
      { keys: ['?'], description: 'Show this help' },
      { keys: ['Esc'], description: 'Close modal / panel' },
      { keys: ['R'], description: 'Refresh data' },
    ]
  },
  {
    section: 'Quick Navigation (Numbers)',
    items: [
      { keys: ['1'], description: 'Go to Dashboard' },
      { keys: ['2'], description: 'Go to Watchlist' },
      { keys: ['3'], description: 'Go to Portfolio' },
      { keys: ['4'], description: 'Go to Alerts' },
    ]
  },
  {
    section: 'Vim-style Navigation (g + key)',
    items: [
      { keys: ['g', 'd'], description: 'Go to Dashboard' },
      { keys: ['g', 'w'], description: 'Go to Watchlist' },
      { keys: ['g', 'r'], description: 'Go to Research' },
      { keys: ['g', 's'], description: 'Go to Screener' },
      { keys: ['g', 'o'], description: 'Go to Options' },
      { keys: ['g', 'm'], description: 'Go to Macro' },
      { keys: ['g', 'p'], description: 'Go to Portfolio' },
      { keys: ['g', 'a'], description: 'Go to Alerts' },
      { keys: ['g', 't'], description: 'Go to Strategy Lab' },
    ]
  },
  {
    section: 'Page-specific',
    items: [
      { keys: ['W'], description: 'Add ticker (on Watchlist)' },
      { keys: ['A'], description: 'Create alert (on Alerts)' },
      { keys: ['P'], description: 'Add position (on Portfolio)' },
      { keys: ['Ctrl', 'S'], description: 'Save (on Strategy editor)' },
    ]
  },
]

export function ShortcutsModal() {
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    const handler = () => setIsOpen(true)
    window.addEventListener('apex-show-shortcuts', handler)
    return () => window.removeEventListener('apex-show-shortcuts', handler)
  }, [])

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      title="Keyboard Shortcuts"
      size="md"
    >
      <div className="shortcuts-content">
        {SHORTCUTS.map((section) => (
          <div key={section.section} className="shortcuts-section">
            <h3 className="shortcuts-section-title">{section.section}</h3>
            <div className="shortcuts-list">
              {section.items.map((item, idx) => (
                <div key={idx} className="shortcut-item">
                  <span className="shortcut-description">{item.description}</span>
                  <div className="shortcut-keys">
                    {item.keys.map((key, keyIdx) => (
                      <span key={keyIdx}>
                        <kbd className="shortcut-key">{key}</kbd>
                        {keyIdx < item.keys.length - 1 && (
                          <span className="shortcut-plus">+</span>
                        )}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Modal>
  )
}

export default ShortcutsModal
